import db from '../Firebase'
import { firebase } from '../Firebase'
import yaml from 'js-yaml'

/**
 * Finds a Firestore doc from the allDocs snapshot by document ID.
 */
function findDoc(allDocs, docId) {
  return allDocs.docs.find(d => d.id === docId)
}

function findDocByType(allDocs, type) {
  return allDocs.docs.find(d => d.get('type') === type)
}

/**
 * Sets a nested field via dot-path on a plain object.
 * e.g. setByPath({}, 'name.en', 'Foo') → { name: { en: 'Foo' } }
 * For Firestore, dot-paths in update() work natively, so we use them directly.
 */

/**
 * Execute a list of approved operations as a single Firestore batch.
 * Returns { success: true } or { success: false, error: string }.
 */
export default async function executeOperations(operations, allDocs) {
  try {
    const batch = db.batch()
    const allRef = db.collection('all')

    let newSubMenuId = null
    let newProductId = null
    let newProductModgroups = []

    for (const op of operations) {
      switch (op.type) {
        case 'UPDATE_FIELD': {
          const doc = findDoc(allDocs, op.docId)
          if (!doc) return { success: false, error: `Document not found: ${op.docId}` }
          batch.update(allRef.doc(op.docId), { [op.field]: op.newValue })
          break
        }

        case 'UPDATE_MODIFIER': {
          const resolvedDocId =
            op.docId === 'NEW_PRODUCT' && newProductId
              ? newProductId
              : op.docId

          const doc = findDoc(allDocs, resolvedDocId)
          const isNewProduct = !doc && resolvedDocId === newProductId
          if (!doc && !isNewProduct) {
            return { success: false, error: `Document not found: ${op.docId}` }
          }

          const currentModgroups = isNewProduct
            ? newProductModgroups
            : (doc.get('modgroups') || [])
          const newModgroups = applyModifierAction(currentModgroups, op)

          if (isNewProduct) {
            newProductModgroups = newModgroups
          } else {
            const newYaml = yaml.safeDump(newModgroups)
            batch.update(allRef.doc(resolvedDocId), {
              modgroups: newModgroups,
              modgroupsYAMLString: newYaml,
            })
          }
          break
        }

        case 'MOVE_IN_ORDER': {
          const doc = findDoc(allDocs, op.docId)
          if (!doc) return { success: false, error: `Document not found: ${op.docId}` }
          const currentArray = (doc.get(op.arrayField) || []).filter(id => id !== op.moveId)
          const index = Math.max(0, Math.min(op.toIndex, currentArray.length))
          currentArray.splice(index, 0, op.moveId)
          batch.update(allRef.doc(op.docId), { [op.arrayField]: currentArray })
          break
        }

        case 'CREATE_SUBMENU': {
          const shopDoc = findDocByType(allDocs, 'shop')
          if (!shopDoc) return { success: false, error: 'Shop document not found' }

          const newSubMenuRef = allRef.doc()
          newSubMenuId = newSubMenuRef.id

          const subMenuData = {
            type: 'subMenu',
            shopId: shopDoc.get('shopId'),
            orderIndex: [],
            draft: false,
            ...op.subMenuData,
          }
          batch.set(newSubMenuRef, subMenuData)

          const currentOrder = shopDoc.get('subMenusOrderIndex') || []
          batch.update(shopDoc.ref, {
            subMenusOrderIndex: [...currentOrder, newSubMenuRef.id],
          })
          break
        }

        case 'CREATE_PRODUCT': {
          const resolvedSubMenuId =
            op.subMenuId === 'NEW_SUBMENU' && newSubMenuId
              ? newSubMenuId
              : op.subMenuId

          const subMenuDoc = findDoc(allDocs, resolvedSubMenuId)
          const shopDoc = findDocByType(allDocs, 'shop')
          if (!shopDoc) return { success: false, error: 'Shop document not found' }

          const newProductRef = allRef.doc()
          newProductId = newProductRef.id
          newProductModgroups = []
          const productData = {
            type: 'product',
            shopId: shopDoc.get('shopId'),
            subMenuId: resolvedSubMenuId,
            draft: false,
            ...op.productData,
          }
          batch.set(newProductRef, productData)

          if (subMenuDoc) {
            const currentOrder = subMenuDoc.get('orderIndex') || []
            batch.update(allRef.doc(resolvedSubMenuId), {
              orderIndex: [...currentOrder, newProductRef.id],
            })
          } else if (resolvedSubMenuId === newSubMenuId) {
            batch.update(allRef.doc(resolvedSubMenuId), {
              orderIndex: firebase.firestore.FieldValue.arrayUnion(newProductRef.id),
            })
          }
          break
        }

        default:
          return { success: false, error: `Unknown operation type: ${op.type}` }
      }
    }

    if (newProductId && newProductModgroups.length > 0) {
      const newYaml = yaml.safeDump(newProductModgroups)
      batch.set(allRef.doc(newProductId), {
        modgroups: newProductModgroups,
        modgroupsYAMLString: newYaml,
      }, { merge: true })
    }

    await batch.commit()
    return { success: true }
  } catch (err) {
    console.error('executeOperations error:', err)
    return { success: false, error: err.message }
  }
}

function applyModifierAction(modgroups, op) {
  const clone = JSON.parse(JSON.stringify(modgroups))

  switch (op.action) {
    case 'remove_modifier': {
      const mg = clone.find(g => g.id === op.modgroupId)
      if (mg) {
        mg.modifiers = (mg.modifiers || []).filter(m => m.id !== op.modifierId)
      }
      break
    }

    case 'add_modifier': {
      let mg = clone.find(g => g.id === op.modgroupId)
      if (!mg) {
        mg = { id: op.modgroupId, name: op.modgroupName || op.modgroupId, type: 'single', modifiers: [] }
        clone.push(mg)
      }
      mg.modifiers = mg.modifiers || []
      mg.modifiers.push({ id: op.modifierId || `mod_${Date.now()}`, name: op.newValue || op.modifierName || '', price: op.price || 0 })
      break
    }

    case 'rename_modifier': {
      const mg = clone.find(g => g.id === op.modgroupId)
      if (mg) {
        const mod = (mg.modifiers || []).find(m => m.id === op.modifierId)
        if (mod) mod.name = op.newValue
      }
      break
    }

    case 'rename_modgroup': {
      const mg = clone.find(g => g.id === op.modgroupId)
      if (mg) mg.name = op.newValue
      break
    }

    case 'remove_modgroup': {
      return clone.filter(g => g.id !== op.modgroupId)
    }

    default:
      break
  }

  return clone
}
